import TipTapLink from "@tiptap/extension-link";
import { useTheme, useMediaQuery } from "@mui/material";
import { CSSProperties } from "@mui/styles";
import { Box } from "@mui/system";
import { EditorContent, useEditor } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import { TableKit } from "@tiptap/extension-table";
import React, { useEffect } from "react";
import {
  useBlockToFocus,
  useNotebookPageAccess,
  useSelectedNotebookPageIdMap,
  useSetNoteEdit,
} from "../../../hook/page";
import { HtmlBlock } from "../../../types/notebook/note/block/html";
import { useIsDarkMode } from "../../../hook/setting";
import CodeBlockShiki from "tiptap-extension-code-block-shiki";
import { useActiveEditor } from "../../../context/activeEditor";

export const HtmlBlockComponent: React.FC<{
  block: HtmlBlock;
  blockIndex: number;
}> = React.memo(({ blockIndex, block }) => {
  const [blockToFocus, setBlockToFocus] = useBlockToFocus();
  const theme = useTheme();
  const { selectedNotebookPageId } = useSelectedNotebookPageIdMap();
  const { writeAccess } = useNotebookPageAccess(selectedNotebookPageId);
  const flushEditHtml = useSetNoteEdit(blockIndex);
  const { isDarkMode } = useIsDarkMode();
  const { setActiveEditor } = useActiveEditor();
  const isMediumScreen = useMediaQuery(theme.breakpoints.up("md"));
  const paddingSx: CSSProperties = isMediumScreen
    ? { padding: "2rem" }
    : { padding: "0.5rem" };
  const styles = { ...paddingSx };

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        codeBlock: false,
      }),
      TipTapLink.configure({
        autolink: true,
        HTMLAttributes: {
          class: "tiptap-link",
        },
      }),
      CodeBlockShiki.configure({
        defaultTheme: isDarkMode ? "github-dark" : "github-light",
      }),
      TableKit.configure({ table: { resizable: true } }),
    ],
    content: block.html,
    onFocus: ({ editor }) => setActiveEditor(editor),
    onBlur: ({ editor }) =>
      setActiveEditor((prev) => (prev === editor ? null : prev)),
    onUpdate: ({ editor }) => {
      const html = editor.getHTML();
      flushEditHtml(html);
    },
  });

  useEffect(() => {
    return () => {
      setActiveEditor((prev) => (prev === editor ? null : prev));
    };
  }, [editor]);

  useEffect(() => {
    if (blockToFocus === blockIndex) {
      editor?.commands.focus();
      setBlockToFocus(undefined);
      return;
    }
  }, [blockToFocus]);

  useEffect(() => {
    if (!editor) return;
    const html = editor.getHTML();
    if (html === "" || html === "<p></p>") {
      editor.commands.setContent(block.html);
    }
  }, [block.html, editor]);

  return (
    <Box sx={{ display: "flex", flexDirection: "row", flexGrow: 1 }}>
      {editor && (
        <Box
          onClick={() => editor.chain().focus()}
          sx={{ display: "flex", flexDirection: "column", flexGrow: 1 }}
        >
          {writeAccess ? (
            <EditorContent
              style={{
                display: "flex",
                flexDirection: "column",
                width: "100%",
                paddingLeft: "2rem",
                paddingRight: "2rem",
                outline: "none",
                ...styles,
              }}
              id={`note-block-${blockIndex}`}
              editor={editor}
            />
          ) : (
            <div
              className="note-html-readonly"
              dangerouslySetInnerHTML={{ __html: editor.getHTML() }}
            />
          )}
        </Box>
      )}
    </Box>
  );
});
